import { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Clock, RefreshCw, Play, AlertTriangle } from 'lucide-react';

type ProductKey = 'hub' | 'psa' | 'rmm' | 'voice' | 'defend';
type ProvisionStatus = 'pending' | 'in_progress' | 'provisioned' | 'failed' | 'skipped';

interface ProductProvisionState {
  product: ProductKey;
  status: ProvisionStatus;
  external_id?: string;
  error?: string;
  provisioned_at?: string;
}

interface ProvisioningDoc {
  id: string;
  employee_name: string;
  employee_email: string;
  products: ProductProvisionState[];
  overall_status: 'pending' | 'in_progress' | 'completed' | 'partial' | 'failed';
  started_at: string;
  completed_at?: string;
}

const PRODUCT_LABELS: Record<ProductKey, string> = {
  hub: 'The One Hub',
  psa: 'PSA Technician',
  rmm: 'RMM Device Policy',
  voice: 'Voice Extension',
  defend: 'Defend Risk Profile',
};

const PRODUCT_ICONS: Record<ProductKey, string> = {
  hub: '🏠',
  psa: '🎫',
  rmm: '💻',
  voice: '📞',
  defend: '🛡️',
};

function StatusIcon({ status }: { status: ProvisionStatus }) {
  switch (status) {
    case 'provisioned': return <CheckCircle className="w-4 h-4 text-green-400" />;
    case 'failed': return <XCircle className="w-4 h-4 text-red-400" />;
    case 'in_progress': return <RefreshCw className="w-4 h-4 text-amber-400 animate-spin" />;
    case 'skipped': return <AlertTriangle className="w-4 h-4 text-zinc-400" />;
    default: return <Clock className="w-4 h-4 text-muted-foreground" />;
  }
}

function StatusBadge({ status }: { status: ProvisioningDoc['overall_status'] }) {
  const styles: Record<string, string> = {
    completed: 'bg-green-500/20 text-green-400',
    partial: 'bg-amber-500/20 text-amber-400',
    failed: 'bg-red-500/20 text-red-400',
    in_progress: 'bg-blue-500/20 text-blue-400',
    pending: 'bg-zinc-500/20 text-zinc-400',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${styles[status] ?? styles['pending']}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

interface Props {
  onboardingId: string;
}

export function ProvisioningStatusPanel({ onboardingId }: Props) {
  const [provisioning, setProvisioning] = useState<ProvisioningDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [retrying, setRetrying] = useState<ProductKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/onboarding/${onboardingId}/provision-status`);
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json() as { provisioning: ProvisioningDoc | null };
      setProvisioning(data.provisioning);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [onboardingId]);

  useEffect(() => { void load(); }, [load]);

  async function startProvisioning() {
    setStarting(true);
    setError(null);
    try {
      const res = await fetch(`/api/onboarding/${onboardingId}/provision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Failed to start provisioning');
      const data = await res.json() as ProvisioningDoc;
      setProvisioning(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setStarting(false);
    }
  }

  async function retryProduct(product: ProductKey) {
    setRetrying(product);
    setError(null);
    try {
      const res = await fetch(`/api/onboarding/${onboardingId}/provision/${product}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Failed to retry');
      const data = await res.json() as ProvisioningDoc;
      setProvisioning(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRetrying(null);
    }
  }

  if (loading) return (
    <div className="glass rounded-xl p-5">
      <div className="text-sm text-muted-foreground">Loading provisioning status...</div>
    </div>
  );

  return (
    <div className="glass rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Cross-Product Provisioning</h3>
        {provisioning && <StatusBadge status={provisioning.overall_status} />}
      </div>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      {!provisioning ? (
        <div className="text-center py-4 space-y-3">
          <p className="text-sm text-muted-foreground">
            Auto-provision this employee across Hub, PSA, RMM, Voice, and Defend.
          </p>
          <button
            onClick={() => void startProvisioning()}
            disabled={starting}
            className="flex items-center gap-2 mx-auto px-4 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 text-sm disabled:opacity-50"
          >
            <Play className="w-4 h-4" />
            {starting ? 'Provisioning...' : 'Start Auto-Provisioning'}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {provisioning.products.map(productState => (
            <div key={productState.product} className="flex items-center gap-3 p-3 rounded-lg bg-surface-3">
              <span className="text-base">{PRODUCT_ICONS[productState.product]}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <StatusIcon status={productState.status} />
                  <span className="text-sm font-medium text-foreground">
                    {PRODUCT_LABELS[productState.product]}
                  </span>
                </div>
                {productState.status === 'provisioned' && productState.external_id && (
                  <p className="text-xs text-muted-foreground mt-0.5">ID: {productState.external_id}</p>
                )}
                {productState.status === 'skipped' && productState.error && (
                  <p className="text-xs text-zinc-400 mt-0.5">{productState.error}</p>
                )}
                {productState.status === 'failed' && productState.error && (
                  <p className="text-xs text-red-400 mt-0.5">{productState.error}</p>
                )}
              </div>
              {productState.status === 'failed' && (
                <button
                  onClick={() => void retryProduct(productState.product)}
                  disabled={retrying === productState.product}
                  className="flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 disabled:opacity-50"
                >
                  <RefreshCw className={`w-3 h-3 ${retrying === productState.product ? 'animate-spin' : ''}`} />
                  Retry
                </button>
              )}
            </div>
          ))}

          {provisioning.completed_at && (
            <p className="text-xs text-muted-foreground text-right">
              Completed {new Date(provisioning.completed_at).toLocaleString()}
            </p>
          )}

          {provisioning.overall_status === 'partial' && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-xs text-amber-400">
                Partial provisioning — some products failed. Retry failed items or configure missing API credentials.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
